import { useCallback, useState } from "react";
import { Checkbox, Grid } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import MDButton from "components/MDButton";

import { getTAPickSlipLocation, assignTAPickslipLocation } from "services/data.outbound.service";
import { Success, Error_, LoadingDialog } from "ui-components/Alert";
import { SelectFields } from "ui-components/Form";
import { TableCard } from "ui-components/Layout";
import CheckboxTable2 from "ui-components/tables/CheckboxTable2";
import { CheckboxHeader, CheckboxCell } from "ui-components/tables/components/table";

function PickslipLocation({ customerCode, userOptions, setAlert }) {
  const columns = [
    {
      id: "select",
      header: CheckboxHeader,
      cell: CheckboxCell,
    },
    {
      accessorKey: "pickslipNumber",
      header: "Pickslip Number",
      width: 200,
    },
    {
      accessorKey: "custOrdNbr",
      header: "Cust Order Number",
      width: 200,
    },
    {
      accessorKey: "orderNumber",
      header: "Order Number",
      width: 200,
    },
    {
      accessorKey: "createdBy",
      header: "Created By",
      width: 150,
    },
    {
      accessorKey: "createdOn",
      header: "Created On",
      width: 150,
    },
    {
      accessorKey: "userId",
      header: "RF User Id",
      width: 150,
    },
    {
      accessorKey: "location",
      header: "Location",
      enableColumnFilter: false,
      filterFn: (row, id, data) => {
        const locations = row.original.location.split(",");
        if (data.length === 0) return true;
        return data.some((x) => locations.includes(x));
      },
      width: 150,
    },
    {
      accessorKey: "status",
      header: "Status",
    },
  ];
  const queryClient = useQueryClient();

  const [pickers, setPickers] = useState(null);
  const [pickSlipList, setPickSlipList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [locations, setLocations] = useState([]);
  const { isFetching } = useQuery({
    queryKey: ["task-pickslip-location", { customerCode }],
    queryFn: () => getTAPickSlipLocation({ customerCode }),
    onSuccess: (data) => {
      const { pickSlipList, locList } = data.data.data;
      setPickSlipList(pickSlipList);
      setLocationList(locList);
    },
    enabled: Boolean(customerCode),
  });
  const [columnFilters, setColumnFilters] = useState([]);

  const [selectedIds, setSelectedIds] = useState([]);
  const handleChange = useCallback((data) => {
    setSelectedIds(data);
  }, []);

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const assignMutation = useMutation(assignTAPickslipLocation, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["task-pickslip-location"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });
  const onAssign = () => {
    if (selectedIds.length === 0) return;
    if (pickers === null) return;
    const pickslipIds = selectedIds.map((x) => x.id);
    assignMutation.mutate({ pickslipIds, rfUserId: pickers });
  };

  const assignStatus = assignMutation.status === "loading";
  return (
    <>
      <TableCard sx={{ margin: "2rem 0", padding: 2 }}>
        {(isFetching || assignStatus) && <LoadingDialog />}

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <SelectFields
              field={{
                name: "",
                label: "Search Location",
                value: locations,
                errors: {},
                multiple: true,
              }}
              options={locationList}
              onChange={(e, v) => {
                setColumnFilters([{ id: "location", value: v }]);
                setLocations(v);
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <SelectFields
              field={{ name: "", label: "Picker", value: pickers, errors: {} }}
              options={userOptions}
              onChange={(e, v) => setPickers(v)}
            />
          </Grid>
          <Grid item>
            <MDButton
              color="info"
              variant="gradient"
              onClick={onAssign}
              disabled={selectedIds.length === 0 || pickers === null}
            >
              Assign
            </MDButton>
          </Grid>
        </Grid>
      </TableCard>

      <CheckboxTable2
        data={pickSlipList}
        columns={columns}
        columnFilter={columnFilters}
        setColumnFilter={setColumnFilters}
        onSelectedRowsChange={handleChange}
      />
    </>
  );
}

export default PickslipLocation;
